import React, { useMemo } from "react";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Grid from "components/common/Grid";
import { usePhcTheme } from "../PhcThemeContext";
import {
  normalizePortfolioHighlights,
} from "../portfolioHighlightsData";

const HighlightRow = ({ label, value, tone, isLast }) => {
  const { theme } = usePhcTheme();

  return (
  <Box sx={{ width: "100%" }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1.5}
      sx={{ width: "100%", py: 0.25 }}
    >
      <Text
        sx={{
          fontWeight: 400,
          fontSize: { xs: 14, md: 16 },
          color: theme.textPrimary,
          flex: 1,
          minWidth: 0,
          pr: 1,
        }}
      >
        {label}
      </Text>
      <Text
        sx={{
          fontWeight: 500,
          fontSize: { xs: 14, md: 16 },
          color: (theme.highlightTones && theme.highlightTones[tone]) || theme.textPrimary,
          textAlign: "right",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {value ?? "—"}
      </Text>
    </Stack>
    {!isLast && (
      <Box
        sx={{
          mt: 1.5,
          mb: 1.5,
          height: "1px",
          width: "100%",
          bgcolor: theme.sectionBorder,
        }}
      />
    )}
  </Box>
  );
};

const HighlightCard = ({ section }) => {
  const { theme } = usePhcTheme();
  const footerTone = (theme.footerTones && theme.footerTones[section.footer?.tone]) || theme.footerTones?.info;
  const items = section.items || [];

  return (
    <Stack spacing={1.5} sx={{ height: "100%", width: "100%" }}>
      <Text
        sx={{
          fontWeight: 600,
          fontSize: { xs: 16, md: 20 },
          color: theme.textPrimary,
        }}
      >
        {section.title}
      </Text>

      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: { xs: 320, md: 328 },
          borderRadius: theme.radiusCard,
          border: `1px solid ${theme.sectionBorder}`,
          backgroundImage: theme.sectionBg,
          backgroundColor: theme.cardBg,
          boxShadow: theme.cardShadow,
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            pointerEvents: "none",
            boxShadow: theme.insetGlow,
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            px: { xs: 2, md: 2.25 },
            pt: { xs: 2, md: 2.25 },
            pb: 1,
          }}
        >
          {items.map((item, index) => (
            <HighlightRow
              key={item.key || `${item.label}-${index}`}
              label={item.label}
              value={item.value}
              tone={item.tone}
              isLast={index === items.length - 1}
            />
          ))}
        </Box>

        {section.footer?.text && (
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              mt: "auto",
              minHeight: 46,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: footerTone.bg,
              px: 2,
            }}
          >
            <Text
              sx={{
                fontWeight: 700,
                fontSize: { xs: 16, md: 20 },
                color: footerTone.color,
                textAlign: "center",
              }}
            >
              {section.footer.text}
            </Text>
          </Box>
        )}
      </Box>
    </Stack>
  );
};

/**
 * Portfolio Highlights — 
 * API-driven; missing fields show "—". Hidden when no API data.
 */
const PortfolioHighlights = ({ data }) => {
  const { theme } = usePhcTheme();
  const sections = useMemo(
    () => normalizePortfolioHighlights(data),
    [data],
  );

  if (!sections.length) return null;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: theme.radiusSection,
        border: `1px solid ${theme.sectionBorderSoft || theme.sectionBorder}`,
        backgroundImage: theme.sectionBg,
        backgroundColor: theme.cardBg,
        boxShadow: theme.cardShadow,
        px: { xs: 2, md: 2.25 },
        py: { xs: 2.5, md: 3 },
        overflow: "hidden",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.sectionInsetGlow,
        },
      }}
    >
      <Text
        sx={{
          position: "relative",
          zIndex: 1,
          fontWeight: 700,
          fontSize: { xs: 18, md: 24 },
          color: theme.textPrimary,
          mb: { xs: 2, md: 3 },
        }}
      >
        Highlights
      </Text>

      <Grid
        container
        spacing={{ xs: 2, md: 1.75 }}
        sx={{ position: "relative", zIndex: 1 }}
      >
        {sections.map((section) => (
          <Grid item xs={12} md={4} key={section.title}>
            <HighlightCard section={section} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PortfolioHighlights;
